<template>
  <!-- Preloader shown while waiting between steps -->
  <Preloader
    v-if="showPreloader"
    :enabledSportsSite="enabledSportsSite"
  />

  <!-- Place Your Order. First Section would be Jerseys Selection -->
  <PlaceYourOrder
    v-if="showPlaceYourOrder"
    :currentStepIndex="currentStepIndex"

    :currentUser="currentUser"
    :customer="customer"
    :vendor="vendor"
    :rmm="rmm"

    :jerseys="jerseys"
    :oldJerseys="oldJerseys"
    :newJerseys="newJerseys"
    :oldVariants="oldVariants"
    :newVariants="newVariants"
    :variants="variants"

    :shorts="shorts"
    :shortsVariants="shortsVariants"

    :socks="socks"
    :socksVariants="socksVariants"
    :oldSocks="oldSocks"
    :newSocks="newSocks"
    :oldSocksVariants="oldSocksVariants"
    :newSocksVariants="newSocksVariants"

    :balls="balls"
    :ballsVariants="ballsVariants"

    :medals="medals"
    :medalsVariants="medalsVariants"

    :notifications="notifications"
    :enabledSportsSite="enabledSportsSite"
    :startGuide="startGuide"
  />

  <TeamSizeBreakdownModal
    v-if="showTeamBreakDownSizeModal"
    :customer="customer"
    :visible="showTeamBreakDownSizeModal"
    @close="handleTeamSizeContinue"
  />

  <!-- Main "Place Your Order" UI -->
  <div v-if="showPlaceOrder" class="row">
    <div class="col-12">
      <div class="card radius-20 ff-mont tim-card">
        <div class="card-body p-0">
          <!-- Intro greeting -->
          <p class="intro-title">
            <span>&#128075</span> {{ customer.translations.hi }}, {{ currentUser.name }}
          </p>
          <h5 style="color: #3F2021;" class="fw-300 mb-0">
            {{ customer.translations.welcome }} {{ currentUser.name }} {{ customer.translations.from }} {{ customer.club_name }}!
          </h5>
        </div>
      </div>
    </div>
  </div>

  <!-- Full Order Flow UI (with notifications and category cards) -->
  <div v-if="showPlaceOrder" class="row match-height allBlur justify-content-center">
    <div class="col-xl-12 col-lg-12">
      <div class="card active-users radius-20 tim-card">
        <div class="notification-container">
          <!-- Place Your Section + Welcome Text Section -->
          <div class="card-header border-0 bg-transparent text-center">
            <h3 class="intro-title mb-2">
              {{ customer.translations.place_your_order }}
            </h3>
            <h5 style="color: #3F2021;" class="mb-0">
              {{ customer.translations.welcome }} {{ currentUser.name }} {{ customer.translations.from }} {{ customer.club_name }}!
            </h5>
          </div>

          <!-- Notification Section -->
          <div
            v-for="(notification, index) in sanitizedNotifications"
            :key="index"
          >
            <div class="alert alert-warning no-autodismiss" role="alert">
              <p style="text-align: left;" v-html="notification.sanitizedBody"></p>
            </div>
          </div>
        </div>

        <div class="place-container">
          <!-- Cards for Soccer or Hockey options -->
          <div class="row">
            <SoccerAndHockeyCards
              :currentUser="currentUser"
              :enabledSportsSite="enabledSportsSite"
            />
          </div>

          <!-- Button to start order, visible only for verified users -->
          <div class="card-content">
            <button
              v-if="currentUser.verified === 'Verified'"
              @click="startOrder"
              class="btn primary-btn w-100 text-center py-14"
            >
              {{ customer.translations.start_your_order }}
            </button>
            <!-- If user is not verified -->
            <p v-else class="text-danger">Account not verified</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import DOMPurify from 'dompurify'
  import SoccerAndHockeyCards from './SoccerAndHockeyCards.vue';
  import Preloader from './Preloader.vue';
  import PlaceYourOrder from './PlaceYourOrder.vue';
  import SizeGuideModal from './SizeGuideModal.vue';
  import TeamSizeBreakdownModal from './TeamSizeBreakdownModal.vue';

  export default {
    components: {
      SoccerAndHockeyCards,
      Preloader,
      PlaceYourOrder,
      SizeGuideModal,
      TeamSizeBreakdownModal,
    },
    props: {
      currentUser: {
        type: Object,
        required: true
      },
      customer: {
        type: Object,
        required: true
      },
      vendor: {
        type: Object,
        required: true
      },
      rmm: {
        type: Object,
        required: true
      },

      jerseys: {
        type: Object,
        required: true
      },
      oldJerseys: {
        type: Array,
        default: null
      },
      newJerseys: {
        type: Array,
        default: null
      },
      oldVariants: {
        type: Array,
        default: null
      },
      newVariants: {
        type: Array,
        default: null
      },
      variants: {
        type: Object,
        required: true
      },
      shorts: {
        type: Object,
        required: true
      },
      shortsVariants: {
        type: Object,
        required: true
      },
      socks: {
        type: Object,
        required: true
      },
      socksVariants: {
        type: Object,
        required: true
      },
      oldSocks: {
        type: Array,
        default: null
      },
      newSocks: {
        type: Array,
        default: null
      },
      oldSocksVariants: {
        type: Array,
        default: null
      },
      newSocksVariants: {
        type: Array,
        default: null
      },
      balls: {
        type: Object,
        required: true
      },
      ballsVariants: {
        type: Object,
        required: true
      },
      medals: {
        type: Object,
        required: true
      },
      medalsVariants: {
        type: Object,
        required: true
      },

      notifications: {
        type: Array,
        required: true
      },
      enabledSportsSite: {
        type: String,
        required: true
      }
    },
    data() {
      return {
        currentStepIndex: 0, // Initial Page After Starting Order, i.e Jerseys

        // Controls which section of the UI is visible
        showPlaceOrder: true,       // Start here
        showPreloader: false,       // Temporary loading step (2 seconds)
        showPlaceYourOrder: false,  // PlaceYourOrder step
        showTeamBreakDownSizeModal: false,
        startGuide: false
      };
    },
    computed: {
      sanitizedNotifications() {
        // Sanitize notification body HTML to prevent XSS
        return this.notifications.map(notification => {
          return {
            ...notification,
            sanitizedBody: DOMPurify.sanitize(notification.body)
          }
        })
      }
    },
    emits: ['toggleAppContent'],
    methods: {
      // User clicks "Start Your Order"
      startOrder() {
        this.placeOrder();
      },

      handleTeamSizeContinue() {
        this.showTeamBreakDownSizeModal = false;
        this.startGuide = true;
      },

      // Transition to Jerseys Selection page
      placeOrder() {
        this.showPlaceOrder = false;
        this.showPreloader = true;
        this.showPlaceYourOrder = false;

        setTimeout(() => {
          this.showPreloader = false;
          this.showPlaceYourOrder = true;

          this.$nextTick(() => {
            this.showTeamBreakDownSizeModal = true;
          });
        }, 2000);
      }
    }
  };
</script>
